/**
 * The inherited-field display contract (F2) — the ONE rule the plan edit modal
 * and the per-tenant Plan tab both render by, so an inherited value looks the
 * same everywhere.
 *
 * The inheritance chain is **platform-default → plan → tenant-override**. At any
 * layer a field either holds an explicit SAVED value or INHERITS the value from
 * the layer above. This helper turns `(saved, inherited)` into the three things
 * a field needs to render:
 *
 *   - `placeholder` — the inherited value, shown greyed when the field inherits.
 *   - `value`       — the value to put IN the input: the saved value when it is a
 *                     genuine override, else `null` (render the placeholder).
 *   - `resetTo`     — the inherited value, present ONLY when overridden, so the
 *                     "Reset to default <resetTo>" affordance shows iff there is
 *                     something to reset.
 *
 * ── "Overridden" means saved AND different, judged against the DB ─────────
 *
 * A field is overridden only when a value is SAVED and it DIFFERS from the
 * inherited value. Two consequences the plan calls out:
 *   - a saved value that equals the inherited one is NOT an override — reset
 *     would be a no-op, so the button must not appear (and the input shows the
 *     placeholder, not a redundant copy of the default);
 *   - the decision is made against the SAVED value, never a dirty draft — the
 *     reset affordance reflects what is persisted, so a user typing the default
 *     into an inheriting field does not conjure a phantom override.
 *
 * A real `0` (or any falsy-but-present value) is a distinct saved value, so
 * presence is tested with a null check, never truthiness.
 */

export interface InheritedFieldState<T> {
  /** A value is saved AND differs from the inherited value. */
  overridden: boolean
  /** The inherited value — the input's placeholder. */
  placeholder: T
  /** What to show in the input: the saved override, or `null` to show the
   *  placeholder. */
  value: T | null
  /** The value "Reset to default" restores — the inherited value when
   *  overridden, else `null` (nothing to reset). */
  resetTo: T | null
}

/**
 * Compute the render state of one inherited field.
 *
 * @param saved     the persisted value at this layer, or `null`/`undefined` when
 *                  the field inherits.
 * @param inherited the value from the layer above (never null — it is a resolved
 *                  default or the plan value).
 * @param equals    optional comparator for structured values; defaults to
 *                  `Object.is` (correct for primitives, including `0`).
 */
export function inheritedFieldState<T>(
  saved: T | null | undefined,
  inherited: T,
  equals: (a: T, b: T) => boolean = Object.is,
): InheritedFieldState<T> {
  const hasSaved = saved != null
  const overridden = hasSaved && !equals(saved as T, inherited)
  return {
    overridden,
    placeholder: inherited,
    value: overridden ? (saved as T) : null,
    resetTo: overridden ? inherited : null,
  }
}
